/**
 * vibecheck ship — Pre-deployment readiness check
 */

import * as fs from 'fs';
import * as path from 'path';
import { execSync } from 'child_process';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions, ShipCheck, ShipVerdict, ShipResult, CheckStatus } from '../types';
import { scanFile, resetScanner } from '../core/scanner';
import { walkFiles } from '../core/file-walker';
import { ensureVibecheckDir } from '../core/config';

// Try to load the real 17-engine scanner for deeper analysis
let realScanner: any = null;
async function getRealScanner() {
  if (realScanner === null) {
    try { realScanner = await import('../../../engines'); } catch { realScanner = false; }
  }
  return realScanner || null;
}
import { detectProject } from '../core/project-detect';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

export function registerShip(program: Command): void {
  const cmd = program
    .command('ship')
    .description('Pre-deployment readiness check')
    .option('--strict', 'Fail on any warning (not just errors)')
    .option('--format <type>', 'Output format for report')
    .option('--skip <checks>', 'Skip specific checks (comma-separated)');

  cmd.command('check').description('Run all pre-ship checks (default)').action(async (_o: any, c: Command) => {
    await runShip({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), mode: 'check' });
  });

  cmd.command('report').description('Generate a detailed ship-readiness report').action(async (_o: any, c: Command) => {
    await runShip({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), mode: 'report' });
  });

  cmd.command('checklist').description('Interactive checklist walkthrough').action(async (_o: any, c: Command) => {
    await runShip({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts()), mode: 'checklist' });
  });

  cmd.action(async (opts: any, command: Command) => {
    await runShip({ ...(command.parent!.opts() as GlobalOptions), ...opts, mode: 'check' });
  });
}

interface ShipOpts extends GlobalOptions {
  mode: 'check' | 'report' | 'checklist';
  strict?: boolean;
  skip?: string;
}

async function runShip(opts: ShipOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const skipSet = new Set((opts.skip || '').split(',').map(s => s.trim()).filter(Boolean));
  resetScanner();

  out.brandHeader('Ship Readiness Check');
  out.nl();

  const checks: ShipCheck[] = [];
  const spinner = ora({ text: 'Running pre-ship checks...', isSilent: opts.quiet }).start();

  // 1. Security scan + 2. Secrets — use real scanner if available
  if (!skipSet.has('security') || !skipSet.has('secrets')) {
    spinner.text = 'Running security scan (17 engines)...';
    const scanner = await getRealScanner();
    let criticals = 0;
    let highs = 0;
    let secretCount = 0;
    let totalFindings = 0;

    if (scanner) {
      try {
        const report = await scanner.scan({
          projectRoot: rootPath,
          engines: ['credentials', 'security', 'fake-features'],
          severityThreshold: 'low',
          parallel: true,
        });
        const findings = report.findings || [];
        criticals = findings.filter((f: any) => f.severity === 'critical').length;
        highs = findings.filter((f: any) => f.severity === 'high').length;
        secretCount = findings.filter((f: any) => f.engine === 'credentials').length;
        totalFindings = findings.length;
      } catch {
        // Fall back to lite scanner
        const files = await walkFiles(rootPath, { maxFiles: 500 });
        for (const file of files) {
          const findings = scanFile(rootPath, file);
          criticals += findings.filter(f => f.severity === 'critical' && f.category === 'security').length;
          highs += findings.filter(f => f.severity === 'high' && f.category === 'security').length;
          secretCount += findings.filter(f => f.ruleId === 'SEC001').length;
        }
      }
    } else {
      const files = await walkFiles(rootPath, { maxFiles: 500 });
      for (const file of files) {
        const findings = scanFile(rootPath, file);
        criticals += findings.filter(f => f.severity === 'critical' && f.category === 'security').length;
        highs += findings.filter(f => f.severity === 'high' && f.category === 'security').length;
        secretCount += findings.filter(f => f.ruleId === 'SEC001').length;
      }
    }

    if (!skipSet.has('security')) {
      const secStatus: CheckStatus = criticals > 0 ? 'failed' : highs > 0 ? 'warning' : 'passed';
      checks.push({
        name: 'Security scan',
        status: secStatus,
        message: criticals > 0 ? `${criticals} critical issues` : highs > 0 ? `${highs} high issues` : `${totalFindings} checked`,
      });
    }

    if (!skipSet.has('secrets')) {
      checks.push({
        name: 'No hardcoded secrets',
        status: secretCount > 0 ? 'failed' : 'passed',
        message: secretCount > 0 ? `${secretCount} secrets found` : undefined,
      });
    }
  }

  // 3. Environment variables
  if (!skipSet.has('env')) {
    spinner.text = 'Validating environment variables...';
    const envExample = path.join(rootPath, '.env.example');
    const envFile = path.join(rootPath, '.env');
    let envStatus: CheckStatus = 'passed';
    let envMsg: string | undefined;
    if (fs.existsSync(envExample)) {
      const exampleVars = parseEnvVars(envExample);
      const actualVars = fs.existsSync(envFile) ? parseEnvVars(envFile) : new Set<string>();
      const missing = [...exampleVars].filter(v => !actualVars.has(v));
      if (missing.length > 0) {
        envStatus = 'warning';
        envMsg = `${missing.length} missing: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`;
      }
    }
    checks.push({ name: 'Environment variables', status: envStatus, message: envMsg });
  }

  // 4. Build check
  if (!skipSet.has('build')) {
    spinner.text = 'Checking build...';
    const project = detectProject(rootPath);
    let buildStatus: CheckStatus = 'passed';
    let buildMsg: string | undefined;
    try {
      const pkgPath = path.join(rootPath, 'package.json');
      if (fs.existsSync(pkgPath)) {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'));
        if (pkg.scripts?.build) {
          execSync(`${project.packageManager} run build --dry-run 2>&1 || true`, { cwd: rootPath, timeout: 10000, stdio: 'pipe' });
        }
      }
    } catch (e: any) {
      if (e.message?.includes('error')) {
        buildStatus = 'failed';
        buildMsg = 'Build command failed';
      }
    }
    // Check TypeScript
    if (fs.existsSync(path.join(rootPath, 'tsconfig.json'))) {
      try {
        execSync('npx tsc --noEmit 2>&1', { cwd: rootPath, timeout: 30000, stdio: 'pipe' });
      } catch {
        buildStatus = 'failed';
        buildMsg = 'TypeScript compilation errors';
      }
    }
    checks.push({ name: 'Build check', status: buildStatus, message: buildMsg });
  }

  // 5. No console.logs
  if (!skipSet.has('console')) {
    spinner.text = 'Checking for console.logs...';
    const files = await walkFiles(rootPath, { maxFiles: 500 });
    let consoleCount = 0;
    for (const file of files) {
      const findings = scanFile(rootPath, file);
      consoleCount += findings.filter(f => f.ruleId === 'QLT001').length;
    }
    checks.push({
      name: 'No console.logs',
      status: consoleCount > 5 ? 'warning' : 'passed',
      message: consoleCount > 0 ? `${consoleCount} found` : undefined,
    });
  }

  // 6. Git status
  if (!skipSet.has('git')) {
    spinner.text = 'Checking git status...';
    let gitStatus: CheckStatus = 'passed';
    let gitMsg: string | undefined;
    try {
      const status = execSync('git status --porcelain', { cwd: rootPath, encoding: 'utf-8' });
      const uncommitted = status.trim().split('\n').filter(l => l.trim().length > 0).length;
      if (uncommitted > 0) {
        gitStatus = 'warning';
        gitMsg = `${uncommitted} uncommitted files`;
      }
    } catch {
      gitStatus = 'skipped';
      gitMsg = 'Not a git repository';
    }
    checks.push({ name: 'Git status', status: gitStatus, message: gitMsg });
  }

  // 7. Test runner
  if (!skipSet.has('tests')) {
    spinner.text = 'Running tests...';
    const project = detectProject(rootPath);
    let testStatus: CheckStatus = 'skipped';
    let testMsg = 'No test script found';
    try {
      const pkgPath = path.join(rootPath, 'package.json');
      if (fs.existsSync(pkgPath)) {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'));
        if (pkg.scripts?.test && pkg.scripts.test !== 'echo "Error: no test specified" && exit 1') {
          try {
            execSync(`${project.packageManager} run test -- --run 2>&1`, { cwd: rootPath, timeout: 60000, stdio: 'pipe' });
            testStatus = 'passed';
            testMsg = 'All tests passed';
          } catch (e: any) {
            const output = e.stdout?.toString() || e.stderr?.toString() || '';
            const failMatch = output.match(/(\d+)\s*(?:failed|failing)/i);
            testStatus = 'failed';
            testMsg = failMatch ? `${failMatch[1]} tests failed` : 'Test suite failed';
          }
        }
      }
    } catch { /* ignore */ }
    checks.push({ name: 'Tests pass', status: testStatus, message: testMsg });
  }

  // 8. Lint check
  if (!skipSet.has('lint')) {
    spinner.text = 'Running linter...';
    let lintStatus: CheckStatus = 'skipped';
    let lintMsg = 'No lint script found';
    try {
      const pkgPath = path.join(rootPath, 'package.json');
      if (fs.existsSync(pkgPath)) {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8'));
        const project = detectProject(rootPath);
        if (pkg.scripts?.lint) {
          try {
            execSync(`${project.packageManager} run lint 2>&1`, { cwd: rootPath, timeout: 30000, stdio: 'pipe' });
            lintStatus = 'passed';
            lintMsg = 'No lint errors';
          } catch (e: any) {
            const output = e.stdout?.toString() || e.stderr?.toString() || '';
            const errorMatch = output.match(/(\d+)\s*error/i);
            const errorCount = errorMatch ? parseInt(errorMatch[1], 10) : 0;
            if (errorCount > 0) {
              lintStatus = 'failed';
              lintMsg = `${errorCount} lint errors`;
            } else {
              lintStatus = 'warning';
              lintMsg = 'Lint warnings found';
            }
          }
        }
      }
    } catch { /* ignore */ }
    checks.push({ name: 'Lint check', status: lintStatus, message: lintMsg });
  }

  // 9. Dependency audit
  if (!skipSet.has('deps')) {
    spinner.text = 'Auditing dependencies...';
    let depStatus: CheckStatus = 'passed';
    let depMsg: string | undefined;
    try {
      const project = detectProject(rootPath);
      const auditCmd = project.packageManager === 'pnpm' ? 'pnpm audit --json 2>&1' : project.packageManager === 'yarn' ? 'yarn audit --json 2>&1' : 'npm audit --json 2>&1';
      const output = execSync(auditCmd, { cwd: rootPath, timeout: 30000, encoding: 'utf-8', stdio: 'pipe' });
      try {
        const audit = JSON.parse(output);
        const vulns = audit.metadata?.vulnerabilities || audit.advisories ? Object.keys(audit.advisories || {}).length : 0;
        const critical = audit.metadata?.vulnerabilities?.critical || 0;
        const high = audit.metadata?.vulnerabilities?.high || 0;
        if (critical > 0) { depStatus = 'failed'; depMsg = `${critical} critical vulnerabilities`; }
        else if (high > 0) { depStatus = 'warning'; depMsg = `${high} high vulnerabilities`; }
        else if (vulns > 0) { depStatus = 'warning'; depMsg = `${vulns} vulnerabilities`; }
      } catch { /* parse error, treat as passed */ }
    } catch (e: any) {
      // npm audit exits non-zero when vulns found
      try {
        const output = e.stdout?.toString() || '';
        if (output.includes('critical')) { depStatus = 'failed'; depMsg = 'Critical vulnerabilities found'; }
        else if (output.includes('high')) { depStatus = 'warning'; depMsg = 'High vulnerabilities found'; }
      } catch { /* ignore */ }
    }
    checks.push({ name: 'Dependency audit', status: depStatus, message: depMsg });
  }

  // 10. Lockfile integrity
  if (!skipSet.has('lockfile')) {
    spinner.text = 'Checking lockfile...';
    let lockStatus: CheckStatus = 'passed';
    let lockMsg: string | undefined;
    const lockFiles = ['pnpm-lock.yaml', 'yarn.lock', 'package-lock.json', 'bun.lockb'];
    const hasLock = lockFiles.some(f => fs.existsSync(path.join(rootPath, f)));
    if (!hasLock) {
      lockStatus = 'warning';
      lockMsg = 'No lockfile found — installs may be non-deterministic';
    } else {
      // Check if lockfile is newer than package.json
      const pkgStat = fs.existsSync(path.join(rootPath, 'package.json')) ? fs.statSync(path.join(rootPath, 'package.json')) : null;
      for (const lf of lockFiles) {
        const lfPath = path.join(rootPath, lf);
        if (fs.existsSync(lfPath) && pkgStat) {
          const lockStat = fs.statSync(lfPath);
          if (pkgStat.mtimeMs > lockStat.mtimeMs) {
            lockStatus = 'warning';
            lockMsg = 'package.json modified after lockfile — run install';
          }
        }
      }
    }
    checks.push({ name: 'Lockfile integrity', status: lockStatus, message: lockMsg });
  }

  // 11. Hallucination scan
  if (!skipSet.has('hallucinations')) {
    spinner.text = 'Scanning for hallucinations...';
    const files = await walkFiles(rootPath, { maxFiles: 300 });
    let halCount = 0;
    for (const file of files) {
      const findings = scanFile(rootPath, file);
      halCount += findings.filter(f => f.category === 'hallucination').length;
    }
    checks.push({
      name: 'No hallucinations',
      status: halCount > 0 ? 'warning' : 'passed',
      message: halCount > 0 ? `${halCount} potential hallucinations` : undefined,
    });
  }

  // 12. TODO/FIXME count
  if (!skipSet.has('todos')) {
    spinner.text = 'Counting TODOs...';
    const files = await walkFiles(rootPath, { maxFiles: 300 });
    let todoCount = 0;
    for (const file of files) {
      const findings = scanFile(rootPath, file);
      todoCount += findings.filter(f => f.ruleId === 'QLT005').length;
    }
    checks.push({
      name: 'TODO/FIXME count',
      status: todoCount > 20 ? 'warning' : 'passed',
      message: todoCount > 0 ? `${todoCount} markers found` : undefined,
    });
  }

  // 13. License file
  if (!skipSet.has('license')) {
    const hasLicense = fs.existsSync(path.join(rootPath, 'LICENSE')) || fs.existsSync(path.join(rootPath, 'LICENSE.md')) || fs.existsSync(path.join(rootPath, 'LICENCE'));
    checks.push({
      name: 'License file',
      status: hasLicense ? 'passed' : 'warning',
      message: hasLicense ? undefined : 'No LICENSE file found',
    });
  }

  // 14. README exists
  if (!skipSet.has('readme')) {
    const hasReadme = fs.existsSync(path.join(rootPath, 'README.md')) || fs.existsSync(path.join(rootPath, 'readme.md'));
    checks.push({
      name: 'README exists',
      status: hasReadme ? 'passed' : 'warning',
      message: hasReadme ? undefined : 'No README.md found',
    });
  }

  // 15. .gitignore has node_modules and .env
  if (!skipSet.has('gitignore')) {
    const giPath = path.join(rootPath, '.gitignore');
    let giStatus: CheckStatus = 'passed';
    let giMsg: string | undefined;
    if (fs.existsSync(giPath)) {
      const gi = fs.readFileSync(giPath, 'utf-8');
      const missing: string[] = [];
      if (!gi.includes('node_modules')) missing.push('node_modules');
      if (!gi.includes('.env') && !gi.includes('.env.local')) missing.push('.env');
      if (missing.length > 0) {
        giStatus = 'warning';
        giMsg = `Missing: ${missing.join(', ')}`;
      }
    } else {
      giStatus = 'warning';
      giMsg = 'No .gitignore file';
    }
    checks.push({ name: '.gitignore check', status: giStatus, message: giMsg });
  }

  // 16. Hardcoded localhost URLs
  if (!skipSet.has('localhost')) {
    spinner.text = 'Checking for localhost URLs...';
    const files = await walkFiles(rootPath, { maxFiles: 200 });
    let localhostCount = 0;
    for (const file of files) {
      const findings = scanFile(rootPath, file);
      localhostCount += findings.filter(f => f.ruleId === 'SEC016').length;
    }
    checks.push({
      name: 'No localhost URLs',
      status: localhostCount > 0 ? 'warning' : 'passed',
      message: localhostCount > 0 ? `${localhostCount} hardcoded localhost URLs` : undefined,
    });
  }

  spinner.stop();

  // Save ship result to .vibecheck/
  try {
    const vcDir = ensureVibecheckDir(rootPath);
    fs.writeFileSync(path.join(vcDir, 'ship-result.json'), JSON.stringify({ checks, timestamp: new Date().toISOString() }, null, 2), 'utf-8');
  } catch { /* ignore */ }
  const duration = performance.now() - start;

  // Determine verdict
  const hasFailed = checks.some(c => c.status === 'failed');
  const hasWarning = checks.some(c => c.status === 'warning');
  const verdict: ShipVerdict = hasFailed ? 'BLOCK' : (hasWarning && opts.strict) ? 'WARN' : hasWarning ? 'WARN' : 'SHIP';

  const result: ShipResult = {
    verdict,
    checks,
    score: Math.round((checks.filter(c => c.status === 'passed').length / checks.length) * 100),
    timestamp: new Date().toISOString(),
    duration,
  };

  if (opts.json) {
    out.jsonOutput(result);
    process.exitCode = verdict === 'BLOCK' ? 2 : verdict === 'WARN' ? 1 : 0;
    return;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PREMIUM SHIP OUTPUT — the most polished output in the entire CLI
  // ═══════════════════════════════════════════════════════════════════════════

  const passed = checks.filter(c => c.status === 'passed').length;
  const failed = checks.filter(c => c.status === 'failed').length;
  const warned = checks.filter(c => c.status === 'warning').length;
  const skipped = checks.filter(c => c.status === 'skipped').length;

  // ── Pre-flight checklist ──
  console.log(colors.bold('  PRE-FLIGHT CHECKLIST'));
  console.log(colors.dim('  ' + '─'.repeat(48)));
  out.nl();

  for (const check of checks) {
    const statusStr = check.status === 'passed'
      ? colors.success(`${icons.pass} PASS`)
      : check.status === 'failed'
      ? colors.error(`${icons.fail} FAIL`)
      : check.status === 'warning'
      ? colors.warning(`${icons.warn} WARN`)
      : colors.dim(`${icons.dash} SKIP`);

    const dots = colors.dim('.'.repeat(Math.max(2, 34 - check.name.length)));
    console.log(`  ${statusStr}  ${check.name} ${dots} ${check.message ? colors.dim(check.message) : ''}`);
  }

  out.nl();
  console.log(colors.dim('  ' + '━'.repeat(48)));
  out.nl();

  // ── Score bar ──
  const barWidth = 40;
  const filledWidth = Math.round((result.score / 100) * barWidth);
  const emptyWidth = barWidth - filledWidth;
  const barColor = result.score >= 80 ? colors.success : result.score >= 50 ? colors.warning : colors.error;
  const barFilled = barColor('█'.repeat(filledWidth));
  const barEmpty = colors.dim('░'.repeat(emptyWidth));
  console.log(`  Ship Score  ${barFilled}${barEmpty}  ${colors.bold(String(result.score))}${colors.dim('/100')}`);
  out.nl();

  // ── Summary line ──
  const summaryParts: string[] = [];
  if (passed > 0) summaryParts.push(colors.success(`${passed} passed`));
  if (failed > 0) summaryParts.push(colors.error(`${failed} failed`));
  if (warned > 0) summaryParts.push(colors.warning(`${warned} warnings`));
  if (skipped > 0) summaryParts.push(colors.dim(`${skipped} skipped`));
  console.log(`  ${summaryParts.join(colors.dim(' · '))}`);
  out.nl();

  // ═══════════════════════════════════════════════════════════════════════════
  // THE VERDICT — big, dramatic, unmistakable
  // ═══════════════════════════════════════════════════════════════════════════

  if (verdict === 'SHIP') {
    // ── SHIP — celebration mode ──
    console.log(colors.success('  ┌──────────────────────────────────────────────┐'));
    console.log(colors.success('  │                                              │'));
    console.log(colors.success('  │') + colors.bold(colors.success('     🚀  S H I P   A P P R O V E D  🚀     ')) + colors.success('│'));
    console.log(colors.success('  │                                              │'));
    console.log(colors.success('  │') + colors.success('       Your code is ready for production       ') + colors.success('│'));
    console.log(colors.success('  │') + colors.success('        All pre-flight checks passed.          ') + colors.success('│'));
    console.log(colors.success('  │                                              │'));
    console.log(colors.success('  └──────────────────────────────────────────────┘'));
    out.nl();

    // ── Badge for GitHub README ──
    const projectName = path.basename(rootPath);
    const badgeUrl = `https://img.shields.io/badge/VibeCheck-Ship%20Approved-22c55e?style=for-the-badge&logo=data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiPjxwYXRoIGQ9Ik0xMiAyMnM4LTQgOC0xMFY1bC04LTMtOCAzdjdjMCA2IDggMTAgOCAxMHoiLz48cG9seWxpbmUgcG9pbnRzPSI5IDEyIDExIDE0IDE1IDEwIi8+PC9zdmc+`;
    const badgeMarkdown = `[![VibeCheck Ship Approved](${badgeUrl})](https://vibecheck.dev)`;

    console.log(colors.bold('  📛 Add this badge to your README:'));
    out.nl();
    console.log(colors.info(`  ${badgeMarkdown}`));
    out.nl();

    // ── Social sharing ──
    const shareText = encodeURIComponent(`🚀 My project "${projectName}" just passed VibeCheck Ship Gate with a score of ${result.score}/100! No hallucinations, no security issues — verified and ready to ship. #VibeCheck #ShipApproved`);
    const shareUrl = encodeURIComponent('https://vibecheck.dev');
    const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;

    console.log(colors.bold('  📣 Share your achievement:'));
    console.log(`  ${colors.dim('Twitter:')}  ${colors.brandDim(twitterUrl)}`);
    console.log(`  ${colors.dim('LinkedIn:')} ${colors.brandDim(linkedInUrl)}`);
    out.nl();

  } else if (verdict === 'WARN') {
    // ── WARN — proceed with caution ──
    console.log(colors.warning('  ┌──────────────────────────────────────────────┐'));
    console.log(colors.warning('  │                                              │'));
    console.log(colors.warning('  │') + colors.bold(colors.warning('     ⚠️  S H I P   W I T H   C A R E  ⚠️     ')) + colors.warning('│'));
    console.log(colors.warning('  │                                              │'));
    console.log(colors.warning('  │') + colors.warning('      Non-blocking warnings detected.          ') + colors.warning('│'));
    console.log(colors.warning('  │') + colors.warning('      Review before production deploy.          ') + colors.warning('│'));
    console.log(colors.warning('  │                                              │'));
    console.log(colors.warning('  └──────────────────────────────────────────────┘'));
    out.nl();

    // Show what needs attention
    const warnChecks = checks.filter(c => c.status === 'warning');
    if (warnChecks.length > 0) {
      console.log(colors.bold('  Items to review:'));
      for (const wc of warnChecks) {
        console.log(`  ${colors.warning(icons.arrow)} ${wc.name}${wc.message ? colors.dim(` — ${wc.message}`) : ''}`);
      }
      out.nl();
    }

  } else {
    // ── BLOCK — hard stop ──
    console.log(colors.error('  ┌──────────────────────────────────────────────┐'));
    console.log(colors.error('  │                                              │'));
    console.log(colors.error('  │') + colors.bold(colors.error('     🚫  D O   N O T   S H I P  🚫            ')) + colors.error('│'));
    console.log(colors.error('  │                                              │'));
    console.log(colors.error('  │') + colors.error('      Critical blockers must be resolved.       ') + colors.error('│'));
    console.log(colors.error('  │') + colors.error('      Deployment is NOT safe.                   ') + colors.error('│'));
    console.log(colors.error('  │                                              │'));
    console.log(colors.error('  └──────────────────────────────────────────────┘'));
    out.nl();

    // Show blockers
    const failedChecks = checks.filter(c => c.status === 'failed');
    if (failedChecks.length > 0) {
      console.log(colors.bold(colors.error('  Blockers:')));
      for (const fc of failedChecks) {
        console.log(`  ${colors.error(icons.fail)} ${fc.name}${fc.message ? colors.dim(` — ${fc.message}`) : ''}`);
      }
      out.nl();
      console.log(`  ${colors.dim('Run')} ${colors.info('vibecheck scan full --fix')} ${colors.dim('to auto-fix what we can.')}`);
      out.nl();
    }
  }

  console.log(colors.dim(`  Completed in ${out.timing(duration)}`));
  out.nl();
  process.exitCode = verdict === 'BLOCK' ? 2 : verdict === 'WARN' ? 1 : 0;
}

function parseEnvVars(filePath: string): Set<string> {
  const vars = new Set<string>();
  try {
    const content = fs.readFileSync(filePath, 'utf-8');
    for (const line of content.split('\n')) {
      const match = line.match(/^([A-Z_][A-Z0-9_]*)=/);
      if (match) vars.add(match[1]);
    }
  } catch { /* ignore */ }
  return vars;
}
